import React from "react";
import { CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDanishDate } from "@/lib/timelineDates";

type ConsolidationCoverageBadgeProps = {
  /** Coverage date (API `resolvedAsOf`) the consolidated text is current through. */
  resolvedAsOf: string | null;
  /** Whether the badge should currently be shown (timeline scrolled out of view). */
  visible: boolean;
  className?: string;
};

/**
 * Floating, pinned, click-through badge showing the consolidation "consolidated up
 * to" (t.o.m.) date in the top-left of the document. Uses a zero-height sticky
 * anchor so the badge overlays the document without shifting layout. Appears only
 * once the timeline — which normally conveys this context — has scrolled out of view.
 * Deliberately shows only the date; the consolidation type stays owned by the
 * toolbar type badge (1:1 with the timeline legend).
 */
const ConsolidationCoverageBadge: React.FC<ConsolidationCoverageBadgeProps> = ({
  resolvedAsOf,
  visible,
  className,
}) => {
  if (!resolvedAsOf || !visible) return null;

  const parsed = new Date(resolvedAsOf);
  if (Number.isNaN(parsed.getTime())) return null;

  return (
    <div
      className="no-print pointer-events-none sticky z-20 h-0 overflow-visible"
      style={{
        top: "calc(var(--legislation-sticky-toolbar-height, 0px) + 0.5rem)",
      }}
      aria-hidden
    >
      <div
        className={cn(
          "absolute left-0 flex w-fit items-center gap-2",
          "rounded-md border border-border bg-background/95 px-2.5 py-1.5 text-sm font-medium text-foreground shadow-md backdrop-blur-sm",
          "animate-in fade-in duration-200",
          className
        )}
      >
        <CalendarClock className="size-4 shrink-0 text-muted-foreground" />
        <span className="whitespace-nowrap">
          t.o.m. {formatDanishDate(parsed)}
        </span>
      </div>
    </div>
  );
};

export default ConsolidationCoverageBadge;
